'use client';
import styled, { css } from 'styled-components';
import { generateContentIconStyle } from '@ui/icons';
import Checkmark from './Checkmark.svg';
import CheckmarkDisabled from './Checkmark-disabled.svg';
import { variants } from './variants';

export const InputWrapper = styled.div`
  height: 28px;
  display: flex;
  align-items: center;
  position: relative;
`;

export const Label = styled.label`
  ${({ theme }) => theme.typography.body}
  margin-left: ${({ theme }) => theme.spacing(7)};
  color: ${({ theme, $status }) =>
    $status !== 'error'
      ? theme.colors.text.primary
      : theme.colors.status.errorPrimary};
  display: flex;
  white-space: nowrap;
`;

export const Input = styled.input`
  ${({ theme }) => theme.visuallyHidden}

  + label {
    display: block;
    cursor: pointer;
    padding-left: ${({ theme }) => theme.spacing(2)};

    &::before {
      content: ' ';
      border-radius: 5px;
      border: 1px solid
        ${({ theme, $status }) =>
          $status !== 'error'
            ? theme.colors.border.dark
            : theme.colors.status.errorPrimary};
      background-color: ${({ theme }) => theme.colors.background.lightest};
      position: absolute;
      top: 0;
      left: 0;
      height: 24px;
      width: 24px;
      visibility: visible;
    }
  }
  &:checked + label::before {
    align-items: center;
    background: ${({ theme }) => theme.colors.neutral.dark};
    border: 1px solid ${({ theme }) => theme.colors.neutral.dark};
    content: url(${Checkmark.src});
    color: ${({ theme }) => theme.colors.text.primary};
    display: flex;
    justify-content: center;
    padding-top: ${({ theme }) => theme.spacing(0.5)};
  }

  &:disabled + label {
    color: ${({ theme }) => theme.colors.text.tertiary};
    &::before {
      background: ${({ theme }) => theme.colors.background.disabled};
      border: 1px solid ${({ theme }) => theme.colors.background.disabled};
    }
  }

  &:focus + label::before,
  &:focus:checked + label::before {
    border: 2px solid ${({ theme }) => theme.colors.hero.lilac};
    margin: -1px;
  }

  &:disabled:checked + label::before {
    content: url(${CheckmarkDisabled.src});
    background: ${({ theme }) => theme.colors.background.disabled};
    border: 1px solid ${({ theme }) => theme.colors.background.disabled};
  }

  &:hover:enabled + label::before,
  &:hover:enabled:checked + label::before {
    border: 2px solid ${({ theme }) => theme.colors.hero.lilac};
    margin: -1px;
  }
`;

export const RoundedCheckboxLabel = styled.label`
  ${({ theme }) => theme.typography.body}
  align-items: center;
  border-radius: 200px;
  display: grid;
  grid-template-columns: 30px 1fr;
  height: ${props => (props.$height ? `${props.$height}px` : '62px')};
  padding: 0 ${({ theme }) => theme.spacing(4)};
  text-align: center;

  ${({ checked, variant, theme }) => {
    const status = checked ? 'checked' : 'unchecked';

    return css`
      background: ${props =>
        props.disabled
          ? 'transparent'
          : theme.colors.get(variants[variant][status].background)};
      color: ${theme.colors.get(variants[variant][status].text)};
      border: 1px solid
        ${props =>
          props.disabled
            ? 'transparent'
            : theme.colors.get(variants[variant][status].border)};
      &:hover,
      &:focus-within {
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
        border: 2px solid
          ${props =>
            props.disabled
              ? 'transparent'
              : theme.colors.get(variants[variant].hoverColor)};
        margin: 0 -1px;
      }

      &::before {
        ${({ checked }) =>
          checked
            ? generateContentIconStyle('radiobox-marked')
            : generateContentIconStyle('radiobox-blank')};
        ${({ disabled }) => disabled && generateContentIconStyle('close')};
        width: 20px;
        height: 20px;
        background-color: ${({ theme }) =>
          theme.colors.get(variants[variant][status].iconBackground)};
      }
    `;
  }}
`;

export const InputContainer = styled.div`
  display: block;
`;
